import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GetCategorizedTasks, ensureCategorizedTasks, selectProject } from '../../redux/projectDataSlice';
import { Menu, MenuTrigger, MenuPopover } from '@fluentui/react-components';
import Select from 'react-select';
import { Dropdown } from 'react-bootstrap';
import styles from './css/index.module.css';
import { ApprovalsApp16Regular, Target16Regular, BuildingBank16Regular, HourglassOneQuarter16Regular, ErrorCircle16Filled, Filter16Regular } from '@fluentui/react-icons';
const ProjectTaskStatus = ({ getStatusColor, formatLastUpdated, setShowTaskStatus, statusOptions, projectTypes }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const projectData = useSelector(state => state.projectData);
    const { user } = useSelector(state => state.user);

    const { categorizedTasks } = projectData;

    // Handle case where categorizedTasks might be undefined
    const { data, filters } = categorizedTasks || {
        data: null,
        loading: false,
        error: null,
        filters: { workstream_repr: '', status: '' },
    };

    // Filter state for the dropdown
    const [localFilters, setLocalFilters] = React.useState({
        status: [],
        projectType: [],
    });

    const safeStatusOptions = Array.isArray(statusOptions) ? statusOptions : [];
    const safeProjectTypes = Array.isArray(projectTypes) ? projectTypes : [];

    useEffect(() => {
        // Ensure categorizedTasks exists in Redux state (migration)
        dispatch(ensureCategorizedTasks());
    }, [dispatch]);

    useEffect(() => {
        // Only dispatch if we have valid filters
        if (filters) {
            dispatch(GetCategorizedTasks(filters));
        }
    }, [dispatch, filters]);

    // Log data to console when it changes
    useEffect(() => {
        if (data) {
            console.log('Categorized Tasks Data:', data);
        }
    }, [data]);

    // Unique tag names only (no IDs, no repetitions)
    const techDDTags = [
        'Setup Project In Lens',
        'Setup Project In Teams',
        'NDA',
        'Outside In Schedule',
        'Internal Cost Model',
        'Proposal',
        'Miro Diagram',
        'Schedule & Meetings',
        'IRL - Document Request',
        'IRL - Q&A',
        'Target Financial Model',
        'Cyber Security Q&A',
        'Output',
        'Service Quality',
        'Invoicing',
    ];

    console.log('Unique Tags:', techDDTags);

    // Calculate counts for status options
    const getStatusOptionsWithCounts = () => {
        if (!data?.projects) return safeStatusOptions;

        const statusCounts = {};
        data.projects.forEach(project => {
            const status = project?.status || 'Undefined';
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        return safeStatusOptions.map(option => ({
            ...option,
            label: `${option.label} (${statusCounts[option.value] || 0})`,
        }));
    };

    // Calculate counts for project type options
    const getProjectTypeOptionsWithCounts = () => {
        if (!data?.projects) return safeProjectTypes;

        const projectTypeCounts = {};
        data.projects.forEach(project => {
            const projectType = project?.project_type;
            if (projectType) {
                projectTypeCounts[projectType] = (projectTypeCounts[projectType] || 0) + 1;
            }
        });

        return safeProjectTypes.map(option => ({
            ...option,
            label: `${option.label} (${projectTypeCounts[option.value] || 0})`,
        }));
    };

    // Filter projects based on current filters
    const getFilteredProjects = () => {
        if (!data?.projects) return [];

        return data.projects.filter(project => {
            // Apply status filter
            if (
                localFilters.status.length > 0 &&
                !localFilters.status.some(option => {
                    if (option.value === 'Undefined') {
                        return !project.status || project.status === null || project.status === '';
                    }
                    return option.value === project.status;
                })
            )
                return false;

            // Apply project type filter
            if (localFilters.projectType.length > 0 && !localFilters.projectType.some(option => option.value === project.project_type)) return false;

            return true;
        });
    };

    const handleFilterChange = (field, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const clearFilters = () => {
        setLocalFilters({
            status: [],
            projectType: [],
        });
    };

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: 'var(--Neutral-Gray-5)',
            borderBottomColor: 'var(--Neutral-Gray-6)',
            minHeight: '32px',
            fontSize: 14,
            boxShadow: state.isFocused ? '0 0 0 1px var(--Brand-Secondary)' : 'none',
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            height: 'auto',
            minHeight: '32px',
            padding: '2px 6px',
            flexWrap: 'wrap',
        }),
        input: (provided, state) => ({
            ...provided,
            margin: '0px',
        }),
        indicatorSeparator: state => ({
            display: 'none',
        }),
        indicatorsContainer: (provided, state) => ({
            ...provided,
            height: '32px',
        }),
        multiValue: (provided, state) => ({
            ...provided,
            backgroundColor: 'var(--Neutral-Gray-5)',
            color: 'var(--Neutral-Gray-8)',
            margin: '1px',
        }),
        multiValueLabel: (provided, state) => ({
            ...provided,
            color: 'var(--Neutral-Gray-8)',
            fontSize: '12px',
        }),
        multiValueRemove: (provided, state) => ({
            ...provided,
            color: 'var(--Neutral-Gray-8)',
            ':hover': {
                backgroundColor: 'var(--Neutral-Gray-6)',
                color: 'var(--Neutral-Gray-9)',
            },
        }),
        menu: (provided, state) => ({
            ...provided,
            zIndex: 9999,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? 'var(--Neutral-Gray-5)' : state.isFocused ? 'var(--Neutral-Gray-3)' : 'white',
            color: state.isSelected ? 'var(--Neutral-Gray-9)' : 'var(--Neutral-Gray-8)',
            fontWeight: 400,
        }),
    };

    const handleNavigateToTasks = projectId => {
        localStorage.setItem('project_id', projectId);
        dispatch(
            selectProject({
                id: projectId,
                callback: () => navigate('/tasks'),
                userType: user?.types,
            }),
        );
    };

    // Get filtered projects
    const filteredProjects = getFilteredProjects();

    return (
        <div>
            <div className=" mb-5 mt-4 d-flex align-items-center justify-content-between">
                <span onClick={() => setShowTaskStatus(false)} className={styles.archiveBtnText} style={{ cursor: 'pointer' }}>
                    ← Back to Project Dashboard
                </span>
                <Dropdown>
                    <Dropdown.Toggle variant="link" className={styles.filterButton} style={{ textDecoration: 'none', color: 'var(--Brand-Secondary)' }}>
                        <Filter16Regular color="var(--Neutral-Gray-8)" />
                        <span className={styles.archiveBtnText + ' ms-1'}>Filter</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className={styles.filterDropdown}>
                        <div className="p-3">
                            <div className="mb-3">
                                <label className={styles.filterLabel}>Status</label>
                                <Select
                                    isMulti
                                    name="status"
                                    options={getStatusOptionsWithCounts()}
                                    value={localFilters.status}
                                    onChange={value => handleFilterChange('status', value)}
                                    styles={selectStyles}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    placeholder="Select statuses..."
                                />
                            </div>
                            <div className="mb-3">
                                <label className={styles.filterLabel}>Project Type</label>
                                <Select
                                    isMulti
                                    name="projectType"
                                    options={getProjectTypeOptionsWithCounts()}
                                    value={localFilters.projectType}
                                    onChange={value => handleFilterChange('projectType', value)}
                                    styles={selectStyles}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    placeholder="Select project types..."
                                />
                            </div>
                            <div className="d-flex justify-content-end">
                                <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {/* Filter Dropdown */}

            <div className={`d-flex overflow-x-auto ${styles.techDDContainer} ${styles.techDDMainContainer}`}>
                <div className={styles.techDDTagsContainer}>
                    {techDDTags?.map(tag => (
                        <div className={styles.techDDCell} key={tag}>
                            {tag}
                        </div>
                    ))}
                </div>
                {filteredProjects?.map(project => (
                    <div key={project.id}>
                        <Menu positioning="below">
                            <MenuTrigger>
                                <div className={styles.techDDProjectCell} data-project-name={project?.project_name}>
                                    <div
                                        className={styles.projectStatus + ' ms-0 '}
                                        style={{ backgroundColor: getStatusColor(project?.status), cursor: 'pointer' }}
                                        onClick={e => e.stopPropagation()}>
                                        {project?.status || '-- --'}
                                    </div>
                                    <p className={styles.techDDProjectTitle}>{project?.project_name}</p>
                                    <p className={styles.techDDProjectProgress}>
                                        {(() => {
                                            // Calculate total completed tasks across all tags for this project
                                            const allTasks = Object.values(project?.tasks || {}).flat();
                                            const totalTasks = allTasks.length;
                                            const completedTasks = allTasks.filter(task => task?.status === 'COMPLETED').length;
                                            const percentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
                                            return `${completedTasks} out of ${totalTasks} (${percentage}%)`;
                                        })()}
                                    </p>
                                    <span className={styles.techDDProjecetLastUpdated}>
                                        <ApprovalsApp16Regular />
                                        {formatLastUpdated(project?.last_updated)}
                                    </span>
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: 164 }}>
                                <div style={{ padding: '8px' }}>
                                    <div className={styles.techDDProjectType} style={{ marginBottom: '12px' }}>
                                        {project?.project_type}
                                    </div>
                                    <div>
                                        <div className="d-flex gap-2 align-items-center" style={{ marginBottom: 6 }}>
                                            <Target16Regular color="var(--Neutral-Gray-7)" />
                                            <p className={styles.companyName} style={{ color: 'var(--Brand-Primary)' }}>
                                                {project?.company_name || '--'}
                                            </p>
                                        </div>
                                        <div className="d-flex gap-2 align-items-center" style={{ marginBottom: 6 }}>
                                            <BuildingBank16Regular color="var(--Neutral-Gray-7)" />
                                            <p className={styles.companyName} style={{ color: 'var(--Brand-Primary)' }}>
                                                {project?.investor_name || '--'}
                                            </p>
                                        </div>
                                        <div className="d-flex gap-2 align-items-center" style={{ marginBottom: 0 }}>
                                            <HourglassOneQuarter16Regular color="var(--Neutral-Gray-7)" />
                                            <p className={styles.companyName} style={{ color: 'var(--Brand-Primary)' }}>
                                                {project?.deadline || '--'}{' '}
                                                {(() => {
                                                    if (project?.deadline) {
                                                        const deadline = new Date(project.deadline);
                                                        const now = new Date();
                                                        const isDeadlineOverdue = deadline < now;
                                                        return (
                                                            isDeadlineOverdue && (
                                                                <div
                                                                    style={{
                                                                        background: 'white',
                                                                        display: 'inline-block',
                                                                        borderRadius: '100%',
                                                                        width: '9px',
                                                                        height: '10px',
                                                                        position: 'relative',
                                                                        top: '2px',
                                                                    }}
                                                                    className="ms-1">
                                                                    <ErrorCircle16Filled
                                                                        color="var(--RAG-Red)"
                                                                        style={{
                                                                            position: 'relative',
                                                                            top: '-3px',
                                                                            left: '-3px',
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </MenuPopover>
                        </Menu>
                        {techDDTags?.map(item => {
                            const tasksForTag = project?.tasks?.[item] || [];
                            const total = tasksForTag.length;
                            if (!total) {
                                return <div className={styles.techDDTagCell} key={item}></div>;
                            }
                            const completed = tasksForTag.filter(t => t?.status === 'COMPLETED').length;
                            const percent = Math.round((completed / total) * 100);
                            const hasCompleted = completed > 0;
                            const hasWorking = tasksForTag.some(t => t?.status === 'WORKING_ON_IT');
                            const hasBlocked = tasksForTag.some(t => t?.status === 'BLOCKED' || t?.status === 'STUCK');
                            const hasNotStarted = tasksForTag.some(t => t?.status === 'NOT_STARTED');
                            let cellBg = undefined;
                            if (hasBlocked) cellBg = '#B00020'; // red - highest priority
                            else if (hasWorking) cellBg = '#FFC107'; // amber
                            else if (hasCompleted) cellBg = '#198754'; // green
                            else if (hasNotStarted) cellBg = '#C0C6D8'; // gray - lowest priority
                            return (
                                <div
                                    className={styles.techDDTagCell}
                                    key={item}
                                    style={{ background: cellBg, cursor: 'pointer' }}
                                    onClick={() => handleNavigateToTasks(project.id)}>
                                    <span className={styles.techDDTagCellText}>
                                        <b>
                                            {completed} out of {total}{' '}
                                        </b>
                                        ({percent}%)
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProjectTaskStatus;
